'use client';

import { Dialog, type DialogPopupProps } from '@base-ui/react/dialog';
import clsx from 'clsx';
import { type CSSProperties, type ReactNode, useRef } from 'react';
import { X } from 'react-bootstrap-icons';

import {
  DialogPortalContext,
  useDialogPortalContext,
} from '@repo/sds/components/_internal/SDSInternalDialog/dialog-portal-context';

import '@repo/sds/components/_internal/SDSInternalDialog/SDSInternalDialog.scss';

export interface SDSInternalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;

  backdropClassName?: string;
  containerClassName?: string;
  dialogClassName?: string;

  backdropStyle?: CSSProperties;
  containerStyle?: CSSProperties;
  dialogStyle?: CSSProperties;

  hasBackdrop?: boolean;

  showCloseButton?: boolean;
  closeButtonTestId?: string;

  dataTestId?: string;

  ariaLabelledBy?: string;
  ariaDescribedBy?: string;

  /** Where focus goes when the dialog closes. Defaults to the element focused before it opened. */
  finalFocus?: DialogPopupProps['finalFocus'];
}

export function SDSInternalDialog({
  isOpen,
  onClose,
  children,
  backdropClassName,
  containerClassName,
  dialogClassName,
  backdropStyle,
  containerStyle,
  dialogStyle,
  hasBackdrop = true,
  showCloseButton = false,
  closeButtonTestId,
  dataTestId,
  ariaLabelledBy,
  ariaDescribedBy,
  finalFocus,
}: SDSInternalDialogProps) {
  const backdropClasses = clsx('SDSInternalDialog__backdrop', backdropClassName);
  const containerClasses = clsx('SDSInternalDialog__container', containerClassName);
  const dialogClasses = clsx('SDSInternalDialog', dialogClassName);
  const parentDialogPortal = useDialogPortalContext();

  // Stable ref to a div rendered inside Dialog.Popup. SDSPopover (and any
  // other Base UI floating component) reads this via DialogPortalContext to
  // route its portal into the dialog tree, so the popup paints inside the
  // dialog's focus + stacking context instead of getting hidden behind
  // Base UI's modal dialog backdrop.
  const portalContainerRef = useRef<HTMLElement | null>(null);

  return (
    <DialogPortalContext.Provider value={{ portalContainerRef }}>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal container={parentDialogPortal?.portalContainerRef}>
          {hasBackdrop && <Dialog.Backdrop className={backdropClasses} style={backdropStyle} />}
          <div className={containerClasses} style={containerStyle}>
            <Dialog.Popup
              className={dialogClasses}
              style={dialogStyle}
              data-testid={dataTestId}
              aria-labelledby={ariaLabelledBy}
              aria-describedby={ariaDescribedBy}
              finalFocus={finalFocus}
            >
              {showCloseButton && (
                <Dialog.Close
                  className="SDSInternalDialog__close"
                  data-testid={closeButtonTestId}
                  aria-label="Close dialog"
                >
                  <X size={24} />
                </Dialog.Close>
              )}
              {children}
            </Dialog.Popup>
            {/* Sibling of `Dialog.Popup` — outside the popup's overflow:hidden
                + transformed containing block so portaled popover content
                isn't clipped, and outside its flex column flow so the empty
                slot doesn't consume layout space. Still inside Dialog.Portal,
                so the dialog's modal scope (focus trap, inert siblings of
                the portal) continues to apply. */}
            <div
              ref={element => {
                portalContainerRef.current = element;
              }}
              className="SDSInternalDialog__popover-portal"
            />
          </div>
        </Dialog.Portal>
      </Dialog.Root>
    </DialogPortalContext.Provider>
  );
}
